import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { PREP } from '@/constants/testIds';
import { Printer, Plus, X, ArrowLeft, Warning, Phone } from '@phosphor-icons/react';
import TanowMark from '@/components/TanowMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import '@/styles/print.css';

function markerOf(l) {
    if (l.computed_flag) return l.computed_flag;
    if (l.flag === 'Abnormal' || l.flag === 'Needs Review') return 'Needs a look';
    if (l.flag === 'Critical' || l.flag === 'Out of range') return 'Out of range';
    return l.flag || 'Normal';
}
function markerCls(m) {
    return m === 'Out of range' ? 'bg-red-100 text-red-800' :
        m === 'Needs a look' ? 'bg-amber-100 text-amber-900' :
            'bg-emerald-50 text-emerald-700';
}
function calcAge(dob) {
    if (!dob) return null;
    const d = new Date(dob); if (isNaN(d)) return null;
    return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
}

export default function VisitPrepSheet() {
    const { appointmentId } = useParams();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [newQ, setNewQ] = useState('');
    const [busy, setBusy] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ['prep-sheet', appointmentId],
        queryFn: async () => (await api.get(`/appointments/${appointmentId}/prep-sheet`)).data,
    });

    if (isLoading) return <div className="p-10 text-slate-500 text-sm">Loading…</div>;
    if (error || !data) return <div className="p-10 text-slate-500 text-sm">Could not load prep sheet.</div>;

    const { appointment: appt, doctor, patient, prior_appointment, auto_questions, custom_questions,
        custom_checked, auto_checked, active_medications, recent_labs, recent_notes, generated_at } = data;

    const age = calcAge(patient.dob);
    const prepSheetId = data.prep_sheet_id;

    const addQuestion = async () => {
        if (!newQ.trim()) return;
        setBusy(true);
        try {
            await api.post(`/prep-sheets/${prepSheetId}/questions`, { question: newQ.trim() });
            setNewQ('');
            qc.invalidateQueries({ queryKey: ['prep-sheet', appointmentId] });
        } catch { toast.error('Failed'); }
        finally { setBusy(false); }
    };
    const removeCustom = async (idx) => {
        try {
            await api.delete(`/prep-sheets/${prepSheetId}/questions/${idx}`);
            qc.invalidateQueries({ queryKey: ['prep-sheet', appointmentId] });
        } catch { toast.error('Failed'); }
    };
    const toggleCheck = async (key, checked) => {
        try {
            await api.post(`/prep-sheets/${prepSheetId}/check`, { key, checked });
            qc.invalidateQueries({ queryKey: ['prep-sheet', appointmentId] });
        } catch (e) {
            console.error('Prep sheet: could not save checkbox', e);
            toast.error("Couldn't save that checkmark — check your connection and try again.");
        }
    };

    const customChecked = new Set(custom_checked || []);
    const autoChecked = new Set(auto_checked || []);
    const apptDate = appt.scheduled_at ? new Date(appt.scheduled_at) : null;
    const preparedDate = new Date(generated_at || Date.now()).toLocaleDateString();

    return (
        <div data-testid={PREP.root} className="prep-page min-h-screen bg-background">
            {/* Screen-only toolbar */}
            <div className="prep-toolbar sticky top-0 z-30 bg-white border-b border-slate-200 print:hidden">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-sm">
                        <ArrowLeft size={14} /> Back
                    </button>
                    <div className="ml-auto flex items-center gap-2">
                        <Button data-testid={PREP.printButton} onClick={() => window.print()} className="rounded-full bg-primary text-primary-foreground">
                            <Printer size={16} className="mr-1" /> Print / Save as PDF
                        </Button>
                    </div>
                </div>
            </div>

            <div className="prep-content max-w-3xl mx-auto px-4 md:px-8 py-8">
                {/* Header */}
                <div className="flex items-center gap-2 text-primary mb-4">
                    <TanowMark size={22} />
                    <div className="text-[11px] uppercase tracking-[0.2em] font-semibold">Tanow · Visit prep sheet</div>
                </div>
                <h1 className="font-heading text-4xl md:text-5xl text-slate-900">
                    Get ready for {patient.name}&apos;s visit
                </h1>
                <div className="text-slate-600 mt-2">
                    {age != null && <>Age {age} · </>}
                    {appt.provider_name || 'Doctor'}{appt.appt_type && ` · ${appt.appt_type}`}
                    {apptDate && <> · {apptDate.toLocaleString()}</>}
                </div>
                {doctor?.phone && (
                    <div className="text-slate-600 mt-1 text-sm" data-testid="prep-doctor-phone">
                        <Phone size={14} className="inline -mt-0.5 mr-1" />
                        <a href={`tel:${doctor.phone.replace(/[^\d+]/g, '')}`} className="text-primary hover:underline">{doctor.phone}</a>
                        {doctor.fax && <span className="text-slate-500"> · Fax {doctor.fax}</span>}
                    </div>
                )}
                {patient.allergies?.length > 0 && (
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                        <Warning size={18} weight="fill" />
                        <div className="text-sm"><span className="font-semibold uppercase tracking-widest text-[11px] mr-2">Allergies</span>{patient.allergies.join(', ')}</div>
                    </div>
                )}
                <div className="text-xs text-slate-500 mt-2">Prepared {preparedDate} with Tanow</div>

                {/* Questions to ask */}
                <section className="mt-8">
                    <h2 className="font-heading text-2xl text-slate-900">Questions to ask the doctor</h2>
                    <div className="mt-3 space-y-2">
                        {auto_questions.length === 0 && custom_questions.length === 0 && (
                            <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg px-4 py-6 text-center">
                                Nothing has come up automatically. You can still add your own questions below.
                            </div>
                        )}
                        {auto_questions.map(q => {
                            const checked = autoChecked.has(q.key);
                            return (
                                <label key={q.key} data-testid={PREP.question} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                                    <Checkbox
                                        data-testid={PREP.questionCheck}
                                        checked={checked}
                                        onCheckedChange={(v) => toggleCheck(q.key, !!v)}
                                        className="mt-0.5"
                                    />
                                    <span className={`text-base leading-relaxed ${checked ? 'line-through text-slate-400' : 'text-slate-800'}`}>{q.text}</span>
                                </label>
                            );
                        })}
                        {custom_questions.map((q, i) => {
                            const key = `custom:${i}`;
                            const checked = customChecked.has(key);
                            return (
                                <div key={key} data-testid={PREP.question} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                                    <Checkbox
                                        data-testid={PREP.questionCheck}
                                        checked={checked}
                                        onCheckedChange={(v) => toggleCheck(key, !!v)}
                                        className="mt-0.5"
                                    />
                                    <span className={`flex-1 text-base leading-relaxed ${checked ? 'line-through text-slate-400' : 'text-slate-800'}`}>{q}</span>
                                    <button
                                        data-testid={PREP.questionRemove}
                                        onClick={() => removeCustom(i)}
                                        className="text-slate-400 hover:text-red-600 print:hidden"
                                        aria-label="Remove question">
                                        <X size={16} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-3 flex items-center gap-2 print:hidden">
                        <Input
                            data-testid={PREP.addQuestionInput}
                            placeholder="Add your own question…"
                            value={newQ}
                            onChange={e => setNewQ(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') addQuestion(); }}
                        />
                        <Button data-testid={PREP.addQuestionSubmit} onClick={addQuestion} disabled={busy} className="rounded-full bg-primary text-primary-foreground shrink-0">
                            <Plus size={14} className="mr-1" /> Add
                        </Button>
                    </div>
                </section>

                {/* Current medications */}
                <section className="mt-8">
                    <h2 className="font-heading text-2xl text-slate-900">Current medications</h2>
                    {active_medications.length === 0 ? (
                        <div className="text-sm text-slate-500 mt-2">None on file.</div>
                    ) : (
                        <ul className="mt-3 space-y-1.5">
                            {active_medications.map(m => (
                                <li key={m.medication_id} className="text-base text-slate-800">
                                    <span className="font-medium">{m.name}</span>
                                    <span className="text-slate-500"> {m.dose ? `· ${m.dose}` : ''}{m.prescriber ? ` · ${m.prescriber}` : ''}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Recent labs */}
                <section className="mt-8">
                    <h2 className="font-heading text-2xl text-slate-900">Recent lab results</h2>
                    <div className="text-xs text-slate-500 mt-1">
                        {prior_appointment ? `Since the last visit with ${appt.provider_name}` : 'Most recent results'}
                    </div>
                    {recent_labs.length === 0 ? (
                        <div className="text-sm text-slate-500 mt-2">No recent results.</div>
                    ) : (
                        <div className="mt-3 rounded-lg border border-slate-200 overflow-hidden">
                            <div className="grid grid-cols-[110px_1fr_90px_70px_120px_140px] px-3 py-2 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold bg-slate-50">
                                <div>Date</div><div>Test</div><div>Value</div><div>Unit</div><div>Normal</div><div>Marker</div>
                            </div>
                            {recent_labs.map(l => {
                                const m = markerOf(l);
                                return (
                                    <div key={l.lab_id} className="grid grid-cols-[110px_1fr_90px_70px_120px_140px] px-3 py-2 border-b border-slate-100 last:border-b-0 text-sm">
                                        <div className="text-slate-500">{(l.date || '').slice(0, 10)}</div>
                                        <div className="text-slate-900">{l.test_name}</div>
                                        <div className="text-slate-900 font-medium">{l.value}</div>
                                        <div className="text-slate-500">{l.unit || '—'}</div>
                                        <div className="text-slate-500">
                                            {l.ref_low != null && l.ref_high != null ? `${l.ref_low}–${l.ref_high}` :
                                                l.ref_high != null ? `< ${l.ref_high}` :
                                                    l.ref_low != null ? `> ${l.ref_low}` : '—'}
                                        </div>
                                        <div><span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${markerCls(m)}`}>{m}</span></div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Recent care notes */}
                <section className="mt-8">
                    <h2 className="font-heading text-2xl text-slate-900">Recent care notes</h2>
                    {recent_notes.length === 0 ? (
                        <div className="text-sm text-slate-500 mt-2">No notes yet.</div>
                    ) : (
                        <ul className="mt-3 space-y-1.5">
                            {recent_notes.map(n => (
                                <li key={n.log_id} className="text-sm text-slate-800">
                                    <span className="text-slate-500 mr-2">{(n.entry_at || '').slice(0, 10)}</span>
                                    <span className="font-medium">{n.entry_type}</span>
                                    <span className="text-slate-500"> — {(n.note || '').split('\n')[0].slice(0, 140)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <div className="mt-10 text-[11px] text-slate-500 border-t border-slate-200 pt-4 leading-relaxed">
                    <b>In an emergency, call 911.</b> Tanow is not for urgent medical help.
                </div>

                <div className="mt-6 print:hidden text-sm text-slate-500">
                    <Link to={`/members/${patient.patient_id}`} className="text-primary hover:underline">← Back to {patient.name}&apos;s profile</Link>
                </div>
            </div>
        </div>
    );
}
